import { useQuery } from '@tanstack/react-query';
import { useUser } from '../../../hooks/useUser';
import dayjs from '../../../utils/dayjs';
import isToday from 'dayjs/plugin/isToday';
import { isNotNull } from '../../../types/typeUtils';
import hashSum from 'hash-sum';
import { MINUTE_IN_MS } from '../../../utils/utils';
import { trackInsightsException } from '../../../utils/telemetry/appinsights';
import { newAbortSignal } from '../../../utils/axiosUtils';
import { DeliveryMethod } from '@kotipizzagroup/kotipizza-orders-api-client';
import { DeliveryContact, OrderV2 } from '@kotipizzagroup/kotipizza-users-api-client';
import { usersApiClient } from '../../../clients/users-api';
import { getRestaurantDetailsById } from '../../../clients/shops-api';

dayjs.extend(isToday);

export const recentOrdersQueryKey = 'recent-orders';

type RecentOrderData = {
  id: number;
  key: string;
  orderType: DeliveryMethod;
  deliveryEstimate: number | undefined;
  deliveryContact: DeliveryContact;
  restaurantName: string;
  preOrderTime: string;
};

export const useRecentOrders = (
  limit: number = 3
): {
  orders: RecentOrderData[];
  isLoading: boolean;
  error: unknown;
} => {
  const { user, isCCUser } = useUser();

  const userHash = hashSum(user);

  const {
    data: orders,
    isLoading,
    error,
  } = useQuery({
    queryKey: [recentOrdersQueryKey, userHash, limit],
    queryFn: async ({ signal }) => {
      if (!user || isCCUser) {
        return [];
      }

      try {
        const abortSignal = newAbortSignal(signal);

        const response = await usersApiClient.getUserLatestOrders(abortSignal);
        const recentOrders = response.filter((order) => Boolean(order.orderKey));

        if (recentOrders.length < 1) {
          return [];
        }

        const orderData: Array<RecentOrderData | null> = await Promise.all(
          recentOrders.map(async (order) => {
            const { order: orderDetails } = await usersApiClient.getUserOrderById(
              order.orderId.toString(),
              false,
              abortSignal
            );
            if (!isOrderActive(orderDetails)) return null;

            let orderRestaurantName = '';
            if (orderDetails.ShopId) {
              const restaurant = await getRestaurantDetailsById(orderDetails.ShopId, abortSignal);
              orderRestaurantName = restaurant.displayName;
            }

            return {
              id: order.orderId,
              key: order.orderKey,
              orderType: getDeliveryType(Number(orderDetails.deliveryType)),
              deliveryEstimate:
                getDeliveryType(Number(orderDetails.deliveryType)) === DeliveryMethod.Delivery
                  ? orderDetails.deliveryEstimate
                  : orderDetails.pickupEstimate,
              deliveryContact: orderDetails.deliveryContact!,
              restaurantName: orderRestaurantName,
              preOrderTime: orderDetails.preOrderTime || '',
            };
          })
        );
        const result = orderData.filter(isNotNull);
        return result;
      } catch (err) {
        trackInsightsException('FRONTEND_RECENT_ORDERS_ERROR', err);
        return [];
      }
    },
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 10 * MINUTE_IN_MS,
  });

  return {
    orders: orders || [],
    isLoading,
    error,
  };
};

const getDeliveryType = (deliveryType: number): DeliveryMethod => {
  switch (deliveryType) {
    case 0:
      return DeliveryMethod.PickUp;
    case 1:
      return DeliveryMethod.Delivery;
    case 2:
      return DeliveryMethod.EatIn;
    default:
      return DeliveryMethod.Delivery;
  }
};

const isOrderActive = (order: OrderV2): boolean => {
  return Boolean(
    dayjs(order.orderPlaced).isToday() ||
      (order.preOrderTime &&
        (dayjs(order.preOrderTime).isToday() || dayjs(order.preOrderTime).isAfter(dayjs())))
  );
};
